/**
 * API-Client für /api/payouts (Phase I, Kapitel 16 — Auszahlungs-Nachweis &
 * Vorschuss). Bewusst schmal gehalten, analog zu lib/shiftSwaps.ts.
 */

import { apiFetch } from './api';

export type PayoutType = 'regular' | 'advance';
export type PayoutStatus = 'reported' | 'confirmed' | 'rejected';

export interface ApiPayout {
  id: string;
  userId: string;
  type: PayoutType;
  /** Phase L: `null` = für den Aufrufer nicht entschlüsselbar (z. B. Sitzung ohne Lohn-Schlüssel). */
  amount: number | null;
  periodFrom: string | null;
  periodTo: string | null;
  paidByUserId: string | null;
  /**
   * Data-URL des Fotos, `null` wenn keins hochgeladen wurde, oder
   * `'__hidden__'` wenn eins existiert, der Aufrufer es aber laut
   * Kapitel 16.5 nicht sehen darf (fremde Meldung ohne Chef-Rechte).
   */
  photoData: string | null;
  status: PayoutStatus;
  note: string | null;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
}

export interface PendingAdvance extends ApiPayout {
  userName: string;
}

export interface CreatePayoutInput {
  type: PayoutType;
  amount: number;
  periodFrom?: string;
  periodTo?: string;
  paidByUserId?: string | null;
  photoData?: string | null;
  note?: string | null;
}

export const payoutsApi = {
  /** Eigene Meldungen; Chef kann `userId` mitgeben, sonst sieht er alle. */
  list: (token: string, userId?: string) =>
    apiFetch<{ payouts: ApiPayout[] }>(
      `/api/payouts${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`,
      { token },
    ).then((r) => r.payouts),

  pendingAdvances: (token: string) =>
    apiFetch<{ payouts: PendingAdvance[] }>('/api/payouts/pending-advances', { token }).then(
      (r) => r.payouts,
    ),

  create: (token: string, data: CreatePayoutInput) =>
    apiFetch<{ payout: ApiPayout }>('/api/payouts', { method: 'POST', body: data, token }).then(
      (r) => r.payout,
    ),

  decide: (token: string, id: string, decision: 'confirmed' | 'rejected') =>
    apiFetch<{ payout: ApiPayout }>(`/api/payouts/${id}/decision`, {
      method: 'POST',
      body: { decision },
      token,
    }).then((r) => r.payout),
};

/**
 * Komprimiert ein vom User gewähltes Foto (z. B. vom Umschlag) clientseitig
 * auf max. 1280px Kantenlänge und JPEG q≈0.6, bevor es als Data-URL ans
 * Backend geht — hält payout_confirmations.photo_data (LongText) in einem
 * vernünftigen Rahmen, siehe PHOTO_MAX_CHARS in backend/src/routes/payouts.ts.
 */
export function compressPhotoToDataUrl(file: File, maxEdge = 1280, quality = 0.6): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.onload = () => {
      img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxEdge || height > maxEdge) {
          if (width >= height) {
            height = Math.round((height * maxEdge) / width);
            width = maxEdge;
          } else {
            width = Math.round((width * maxEdge) / height);
            height = maxEdge;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas nicht verfügbar'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}
